import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  useTheme,
  Grid,
  Button,
  Divider
} from '@mui/material';
import { Close, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomButton from 'components/Fileds/CustomButton';

const RecipesModal = ({
  open,
  onClose,
  initialData,
  onSubmit,
  loading,
  foodItems = [],
  inventoryItems = [],
  recipes = []
}) => {
  const theme = useTheme();
  const isEdit = Boolean(initialData);
  const title = isEdit ? 'Edit Recipe' : 'Add Recipe';

  const [foodItemId, setFoodItemId] = useState('');
  const [ingredients, setIngredients] = useState([{ inventory_item_id: '', qty: '', unit: 'Kg' }]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setErrors({});
      if (initialData) {
        setFoodItemId(initialData.id);
        const mapped = initialData.recipeIngredients?.map(ing => ({
          inventory_item_id: ing.inventory_item_id,
          qty: ing.qty,
          unit: ing.unit
        })) || [];
        setIngredients(mapped.length > 0 ? mapped : [{ inventory_item_id: '', qty: '', unit: 'Kg' }]);
      } else {
        setFoodItemId('');
        setIngredients([{ inventory_item_id: '', qty: '', unit: 'Kg' }]);
      }
    }
  }, [open, initialData]);

  const foodItemOptions = foodItems
    .filter(item => {
      // If we are editing this recipe, we must allow the selected food item in the dropdown
      if (isEdit && String(item.id) === String(initialData?.id)) {
        return true;
      }
      // Otherwise, hide food items that already have a recipe configured
      const hasRecipe = recipes.some(rec => String(rec.id) === String(item.id));
      return !hasRecipe;
    })
    .map(item => ({
      value: item.id,
      label: item.name
    }));

  const inventoryItemOptions = inventoryItems.map(item => ({
    value: item.id,
    label: `${item.name} (${item.sku_number})`
  }));

  const unitOptions = ["Kg", "gram", "Liter", "Ml", "Piece", "Dozen", "Box"].map(u => ({
    value: u,
    label: u
  }));

  const handleAddIngredient = () => {
    setIngredients([...ingredients, { inventory_item_id: '', qty: '', unit: 'Kg' }]);
  };

  const handleRemoveIngredient = (index) => {
    const updated = ingredients.filter((_, idx) => idx !== index);
    setIngredients(updated.length > 0 ? updated : [{ inventory_item_id: '', qty: '', unit: 'Kg' }]);
  };

  const handleIngredientChange = (index, field, value) => {
    const updated = ingredients.map((ing, idx) => {
      if (idx === index) {
        return { ...ing, [field]: value };
      }
      return ing;
    });
    setIngredients(updated);
  };

  const validate = () => {
    const newErrors = {};
    if (!foodItemId) {
      newErrors.foodItemId = 'Food Item is required';
    }

    const ingErrors = [];
    ingredients.forEach((ing, index) => {
      const err = {};
      if (!ing.inventory_item_id) {
        err.inventory_item_id = 'Required';
      }
      if (!ing.qty || isNaN(Number(ing.qty)) || Number(ing.qty) <= 0) {
        err.qty = 'Invalid Qty';
      }
      if (!ing.unit) {
        err.unit = 'Required';
      }
      if (Object.keys(err).length > 0) {
        ingErrors[index] = err;
      }
    });

    if (ingErrors.length > 0) {
      newErrors.ingredients = ingErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        food_item_id: foodItemId,
        ingredients: ingredients.map(ing => ({
          ...ing,
          qty: Number(ing.qty)
        }))
      });
    }
  };

  // Check if form is valid and all fields are filled
  const isFormValid = foodItemId && ingredients.every(ing => ing.inventory_item_id && ing.qty && Number(ing.qty) > 0 && ing.unit);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', padding: '10px', color: 'black' } }}
    >
      <Box className="d-flex align-items-center justify-content-between" sx={{ m: 0, p: 2 }}>
        <CustomTypography
          variant="h5"
          label={title}
          sx={{ fontSize: '24px', fontWeight: 700, color: theme.palette.typographyLabel.main, mb: 0 }}
        />
        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'grey.500' }}>
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleFormSubmit}>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <CustomTypography
            label="Select Food Item"
            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
          />
          <CustomSelect
            name="food_item_id"
            value={foodItemId}
            onChange={(e) => setFoodItemId(e.target.value)}
            options={foodItemOptions}
            disabled={isEdit}
            placeholder="Select Food Item"
            error={Boolean(errors.foodItemId)}
            helperText={errors.foodItemId}
          />

          <Divider sx={{ my: 3 }} />

          <Box className="d-flex justify-content-between align-items-center" sx={{ mb: 2 }}>
            <CustomTypography
              label="Ingredients"
              sx={{ fontSize: '16px', fontWeight: 700, color: theme.palette.typographyLabel.main }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddIngredient}
              sx={{
                textTransform: 'none',
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Add Ingredient
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 1, display: { xs: 'none', sm: 'flex' } }}>
            <Grid item xs={5}>
              <CustomTypography label="Inventory Item" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }} />
            </Grid>
            <Grid item xs={3}>
              <CustomTypography label="Quantity" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }} />
            </Grid>
            <Grid item xs={3}>
              <CustomTypography label="Unit" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }} />
            </Grid>
            <Grid item xs={1} />
          </Grid>

          <Box sx={{ maxHeight: '280px', overflowY: 'auto', pr: 0.5 }}>
            {ingredients.map((ing, index) => {
              const rowErr = errors.ingredients?.[index] || {};
              return (
                <Grid container spacing={1.5} alignItems="center" key={index} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={5}>
                    <CustomSelect
                      name={`ingredients[${index}].inventory_item_id`}
                      value={ing.inventory_item_id}
                      onChange={(e) => handleIngredientChange(index, 'inventory_item_id', e.target.value)}
                      options={inventoryItemOptions}
                      placeholder="Select Item"
                      error={Boolean(rowErr.inventory_item_id)}
                      helperText={rowErr.inventory_item_id}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <CustomTextField
                      name={`ingredients[${index}].qty`}
                      inputProps={{ inputMode: 'decimal' }}
                      value={ing.qty}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                        handleIngredientChange(index, 'qty', val);
                      }}
                      placeholder="Qty"
                      error={Boolean(rowErr.qty)}
                      helperText={rowErr.qty}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <CustomSelect
                      name={`ingredients[${index}].unit`}
                      value={ing.unit}
                      onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                      options={unitOptions}
                      placeholder="Unit"
                      error={Boolean(rowErr.unit)}
                      helperText={rowErr.unit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1} className="d-flex justify-content-center">
                    <IconButton
                      aria-label="delete"
                      color="error"
                      onClick={() => handleRemoveIngredient(index)}
                      sx={{ bgcolor: '#FFF1F1' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
          </Box>
        </DialogContent>

        <Box className="d-flex align-items-center justify-content-center" sx={{ p: 3, gap: 2 }}>
          <CustomButton
            type="submit"
            disabled={loading || !isFormValid}
            sx={{ borderRadius: "10px", width: "220px", py: 2.1, height: '45px' }}
          >
            {isEdit ? 'Update' : 'Save'}
          </CustomButton>
        </Box>
      </form>
    </Dialog>
  );
};

export default RecipesModal;
